import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fromWithCompany, supabase } from "../config/supabaseClient";
import { postBackendHelper } from "../utils/backendHelperApi";
import { getLocalData } from "../constant/utils/commonUtils";
const localData = JSON.parse(localStorage.getItem("sdfUser"));

// Constants for field limits
const SKILL_NAME_MIN_LENGTH = 10;
const SKILL_NAME_MAX_LENGTH = 100;
const DESCRIPTION_MIN_LENGTH = 20;
const DESCRIPTION_MAX_LENGTH = 500;

// Async thunk for chat functionality
export const executeChatPrompt = createAsyncThunk(
  "chat/executePrompt",
  async (data, { rejectWithValue }) => {
    try {
      return await postBackendHelper({
        prompt: data.prompt,
        message: data.message,
      });
    } catch (error) {
      console.error(
        "Error in executeChatPrompt:",
        error.response?.data || error.message
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for fetching prompts
export const fetchPrompts = createAsyncThunk(
  "chat/fetchPrompts",
  async (category, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("prompt_master")
        .select("*")
        .eq("status", "true")
        .eq("category", category)
        .order("index", { ascending: true });

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addGoal = createAsyncThunk(
  "chat/addGoal",
  async (goal, { rejectWithValue }) => {
    try {
      // Fetch the latest strategic_input_id
      const { data: strategicInputData, error: strategicInputError } =
        await supabase
          .from("strategic_input")
          .select("id")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

      if (strategicInputError) {
        console.error("Error fetching strategic_input:", strategicInputError);
        return rejectWithValue(
          strategicInputError.message || "Failed to fetch strategic input"
        );
      }

      if (!strategicInputData) {
        return rejectWithValue(
          "No strategic input found. Please upload a strategic input file first."
        );
      }

      const goalData = {
        sr_number: goal.sr_number,
        goal: goal.goal,
        benefits: goal.benefits,
        timeline: goal.timeline,
        strategic_input_id: strategicInputData.id,
        created_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("strategic_input_goals")
        .insert([goalData])
        .select();

      if (error) {
        console.error("Error adding goal:", error);
        return rejectWithValue(error.message || "Failed to add goal");
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching goals
export const fetchGoals = createAsyncThunk(
  "chat/fetchGoals",
  async (_, { rejectWithValue }) => {
    try {
      // First get the latest strategic_input_id
      const { data: strategicInputData, error: strategicInputError } =
        await fromWithCompany("strategic_input")
          .select("id")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

      if (strategicInputError) {
        console.error("Error fetching strategic_input:", strategicInputError);
        throw strategicInputError;
      }

      if (!strategicInputData) {
        return [];
      }

      // Then fetch all goals for this strategic_input_id
      const { data, error } = await supabase
        .from("strategic_input_goals")
        .select("*")
        .eq("strategic_input_id", strategicInputData.id)
        .eq("status", "active") // Only fetch active records
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching goals:", error);
        throw error;
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for deleting a goal
export const deleteGoal = createAsyncThunk(
  "chat/deleteGoal",
  async (goalId, { rejectWithValue }) => {
    try {
      const { error } = await supabase
        .from("strategic_input_goals")
        .delete()
        .eq("id", goalId);

      if (error) {
        console.error("Supabase delete error:", error);
        throw error;
      }

      return goalId;
    } catch (error) {
      console.error("Error in deleteGoal:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating a goal
export const updateGoal = createAsyncThunk(
  "chat/updateGoal",
  async (goal, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("strategic_input_goals")
        .update({
          sr_number: goal.sr_number,
          goal: goal.goal,
          benefits: goal.benefits,
          timeline: goal.timeline,
        })
        .eq("id", goal.id)
        .select();

      if (error) {
        console.error("Supabase update error:", error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error("Error in updateGoal:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for adding a critical skill
export const addCriticalSkill = createAsyncThunk(
  "chat/addCriticalSkill",
  async (skill, { rejectWithValue }) => {
    try {
      const userData = getLocalData();
      if (!userData?.company_id) {
        throw new Error("Company ID not found");
      }

      // Get the authenticated user's ID from Supabase auth
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      const userId = authUser?.id || userData.user_id || userData.id;

      if (!userId) {
        throw new Error("User ID not found");
      }

      const skillData = {
        company_id: userData.company_id,
        skill_name: skill.skill_name.trim(),
        description: skill.description?.trim() || "",
        level: skill.level,
        importance: skill.importance,
        created_at: new Date().toISOString(),
        created_by: userId,
      };

      const { data, error } = await supabase
        .from("critical_skills")
        .insert([skillData])
        .select();

      if (error) {
        console.error("Error adding critical skill:", error);
        throw error;
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching critical skills
export const fetchCriticalSkills = createAsyncThunk(
  "chat/fetchCriticalSkills",
  async (_, { rejectWithValue }) => {
    try {
      const userData = getLocalData();
      if (!userData?.company_id) {
        throw new Error("Company ID not found");
      }

      const { data, error } = await supabase
        .from("critical_skills")
        .select("*")
        .eq("company_id", userData.company_id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching critical skills:", error);
        throw error;
      }
      return data || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating a critical skill
export const updateCriticalSkill = createAsyncThunk(
  "chat/updateCriticalSkill",
  async (skill, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("critical_skills")
        .update({
          skill_name: skill.skill_name.trim(),
          description: skill.description?.trim() || "",
          level: skill.level,
          importance: skill.importance,
        })
        .eq("id", skill.id)
        .select();

      if (error) {
        console.error("Supabase update error:", error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error("Error in updateCriticalSkill:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for archiving a critical skill
export const archiveCriticalSkill = createAsyncThunk(
  "chat/archiveCriticalSkill",
  async (skillId, { rejectWithValue }) => {
    try {
      const { error } = await supabase
        .from("critical_skills")
        .delete()
        .eq("id", skillId);

      if (error) {
        console.error("Supabase delete error:", error);
        throw error;
      }

      return skillId;
    } catch (error) {
      console.error("Error in archiveCriticalSkill:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to parse AI response and extract critical skills as JSON
export const parseAndSaveCriticalSkills = createAsyncThunk(
  "chat/parseAndSaveCriticalSkills",
  async (aiResponse, { rejectWithValue, dispatch }) => {
    try {
      const userData = getLocalData();
      if (!userData?.company_id) {
        throw new Error("Company ID not found");
      }

      // Create a prompt to extract skills from AI response in JSON format
      const extractionPrompt = `Extract all critical skills from the following AI response and format them as a JSON array. Each skill must have exactly these fields:
      - skill_name: (string) The name of the skill (${SKILL_NAME_MIN_LENGTH}-${SKILL_NAME_MAX_LENGTH} characters)
      - description: (string) A description of the skill (${DESCRIPTION_MIN_LENGTH}-${DESCRIPTION_MAX_LENGTH} characters)
      - level: (string) One of: "essential", "intermediate", or "expert"
      - importance: (string) A number from "1" to "5" as a string

      Return ONLY a valid JSON array in this exact format:
      [
        {
          "skill_name": "Skill Name 1",
          "description": "Detailed description of the skill",
          "level": "essential",
          "importance": "4"
        },
        {
          "skill_name": "Skill Name 2",
          "description": "Detailed description of the skill",
          "level": "intermediate",
          "importance": "5"
        }
      ]

      AI Response to parse:
      ${aiResponse}

      Important: Return ONLY the JSON array, no additional text or explanation.`;

      const parseData = await postBackendHelper({
        prompt: extractionPrompt,
        message: [
          {
            role: "user",
            content: extractionPrompt,
          },
        ],
      });

      if (!parseData?.data?.content) {
        throw new Error("Failed to parse AI response");
      }

      let content = parseData.data.content;

      // Handle different response types
      let skills;

      // If content is already an array, use it directly
      if (Array.isArray(content)) {
        skills = content;
      }
      // If content is already an object (parsed JSON), check if it's an array
      else if (typeof content === "object" && content !== null) {
        // Check if it's an array-like object or has a data property
        if (Array.isArray(content)) {
          skills = content;
        } else if (Array.isArray(content.data)) {
          skills = content.data;
        } else if (Array.isArray(content.skills)) {
          skills = content.skills;
        } else {
          // Try to convert object to array if it's a single skill
          skills = [content];
        }
      }
      // If content is a string, parse it
      else if (typeof content === "string") {
        let skillsJson = content;

        // Remove markdown code blocks if present
        skillsJson = skillsJson.replace(/```json|```/g, "").trim();

        // Try to extract JSON array from the response
        const jsonMatch = skillsJson.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          skillsJson = jsonMatch[0];
        }

        // Parse the JSON
        try {
          skills = JSON.parse(skillsJson);
        } catch (parseError) {
          console.error("JSON parse error:", parseError);
          console.error("Raw response:", skillsJson);
          throw new Error(
            "Failed to parse skills JSON. Please ensure the AI response contains valid skill data."
          );
        }
      } else {
        throw new Error("Unexpected response format from AI service");
      }

      if (!Array.isArray(skills) || skills.length === 0) {
        throw new Error("No valid skills found in the response");
      }

      // Fetch existing skills to check for duplicates
      const { data: existingSkills, error: fetchError } = await supabase
        .from("critical_skills")
        .select("*")
        .eq("company_id", userData.company_id);

      if (fetchError) {
        console.error("Error fetching existing skills:", fetchError);
        throw fetchError;
      }

      // Get authenticated user ID
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      const userId = authUser?.id || userData.user_id || userData.id;

      if (!userId) {
        throw new Error("User ID not found");
      }

      const results = {
        created: [],
        updated: [],
        errors: [],
      };

      // Process each skill
      for (const skill of skills) {
        try {
          // Validate skill data
          if (!skill.skill_name || !skill.description || !skill.level || !skill.importance) {
            results.errors.push({
              skill: skill.skill_name || "Unknown",
              error: "Missing required fields",
            });
            continue;
          }

          // Validate skill name length
          const trimmedSkillName = skill.skill_name.trim();
          if (trimmedSkillName.length < SKILL_NAME_MIN_LENGTH) {
            results.errors.push({
              skill: skill.skill_name,
              error: `Skill name must be at least ${SKILL_NAME_MIN_LENGTH} characters long`,
            });
            continue;
          }
          if (trimmedSkillName.length > SKILL_NAME_MAX_LENGTH) {
            results.errors.push({
              skill: skill.skill_name,
              error: `Skill name must not exceed ${SKILL_NAME_MAX_LENGTH} characters`,
            });
            continue;
          }

          // Validate description length
          const trimmedDescription = skill.description.trim();
          if (trimmedDescription.length < DESCRIPTION_MIN_LENGTH) {
            results.errors.push({
              skill: skill.skill_name,
              error: `Skill description must be at least ${DESCRIPTION_MIN_LENGTH} characters long`,
            });
            continue;
          }
          if (trimmedDescription.length > DESCRIPTION_MAX_LENGTH) {
            results.errors.push({
              skill: skill.skill_name,
              error: `Skill description must not exceed ${DESCRIPTION_MAX_LENGTH} characters`,
            });
            continue;
          }

          // Validate level
          const validLevels = ["essential", "intermediate", "expert"];
          if (!validLevels.includes(skill.level.toLowerCase())) {
            results.errors.push({
              skill: skill.skill_name,
              error: `Invalid level: ${skill.level}`,
            });
            continue;
          }

          // Validate importance
          const importanceNum = parseInt(skill.importance, 10);
          if (isNaN(importanceNum) || importanceNum < 1 || importanceNum > 5) {
            results.errors.push({
              skill: skill.skill_name,
              error: `Invalid importance: ${skill.importance}`,
            });
            continue;
          }

          // Check if skill with same name exists (case-insensitive)
          const existingSkill = existingSkills?.find(
            (s) =>
              s.skill_name.toLowerCase() ===
              trimmedSkillName.toLowerCase()
          );

          if (existingSkill) {
            // Update existing skill
            const updateResult = await dispatch(
              updateCriticalSkill({
                id: existingSkill.id,
                skill_name: trimmedSkillName,
                description: trimmedDescription,
                level: skill.level.toLowerCase(),
                importance: skill.importance.toString(),
              })
            ).unwrap();
            results.updated.push(updateResult[0]);
          } else {
            // Create new skill
            const createResult = await dispatch(
              addCriticalSkill({
                skill_name: trimmedSkillName,
                description: trimmedDescription,
                level: skill.level.toLowerCase(),
                importance: skill.importance.toString(),
              })
            ).unwrap();
            results.created.push(createResult[0]);
          }
        } catch (skillError) {
          console.error(
            `Error processing skill ${skill.skill_name}:`,
            skillError
          );
          results.errors.push({
            skill: skill.skill_name || "Unknown",
            error: skillError.message || "Unknown error",
          });
        }
      }

      return results;
    } catch (error) {
      console.error("Error in parseAndSaveCriticalSkills:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  messages: [],
  isLoading: false,
  error: null,
  selectedPromptType: null,
  prompts: [],
  promptsLoading: false,
  promptsError: null,
  goals: [],
  goalsLoading: false,
  goalsError: null,
  criticalSkills: [],
  criticalSkillsLoading: false,
  criticalSkillsError: null,
};

// Create slice
const trainingStrategyInputSlice = createSlice({
  name: "trainingStrategyInput",
  initialState,
  reducers: {
    setSelectedPromptType: (state, action) => {
      state.selectedPromptType = action.payload;
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    addUserMessage: (state, action) => {
      state.messages.push({ role: "user", content: action.payload });
    },
    setPrompts: (state, action) => {
      state.prompts = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle executeChatPrompt
      .addCase(executeChatPrompt.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(executeChatPrompt.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload?.data) {
          // Add the assistant's response to messages
          state.messages.push(action.payload.data[1]);
        }
      })
      .addCase(executeChatPrompt.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        // Add error message to chat
        state.messages.push({
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again.",
        });
      })
      // Handle fetchPrompts
      .addCase(fetchPrompts.pending, (state) => {
        state.promptsLoading = true;
        state.promptsError = null;
      })
      .addCase(fetchPrompts.fulfilled, (state, action) => {
        state.promptsLoading = false;
        state.prompts = action.payload;
      })
      .addCase(fetchPrompts.rejected, (state, action) => {
        state.promptsLoading = false;
        state.promptsError = action.payload;
      })

      // Handle fetchGoals
      .addCase(fetchGoals.pending, (state) => {
        state.goalsLoading = true;
        state.goalsError = null;
      })
      .addCase(fetchGoals.fulfilled, (state, action) => {
        state.goalsLoading = false;
        state.goals = action.payload;
      })
      .addCase(fetchGoals.rejected, (state, action) => {
        state.goalsLoading = false;
        state.goalsError = action.payload;
      })
      // Handle addGoal
      .addCase(addGoal.fulfilled, (state, action) => {
        state.goals = [action.payload[0], ...state.goals];
      })
      // Handle deleteGoal
      .addCase(deleteGoal.fulfilled, (state, action) => {
        state.goals = state.goals.filter((goal) => goal.id !== action.payload);
      })
      // Handle updateGoal
      .addCase(updateGoal.fulfilled, (state, action) => {
        const index = state.goals.findIndex(
          (goal) => goal.id === action.payload[0].id
        );
        if (index !== -1) {
          state.goals[index] = action.payload[0];
        }
      })
      // Handle fetchCriticalSkills
      .addCase(fetchCriticalSkills.pending, (state) => {
        state.criticalSkillsLoading = true;
        state.criticalSkillsError = null;
      })
      .addCase(fetchCriticalSkills.fulfilled, (state, action) => {
        state.criticalSkillsLoading = false;
        state.criticalSkills = action.payload;
      })
      .addCase(fetchCriticalSkills.rejected, (state, action) => {
        state.criticalSkillsLoading = false;
        state.criticalSkillsError = action.payload;
      })
      // Handle addCriticalSkill
      .addCase(addCriticalSkill.fulfilled, (state, action) => {
        state.criticalSkills = [action.payload[0], ...state.criticalSkills];
      })
      // Handle archiveCriticalSkill
      .addCase(archiveCriticalSkill.fulfilled, (state, action) => {
        state.criticalSkills = state.criticalSkills.filter(
          (skill) => skill.id !== action.payload
        );
      })
      // Handle updateCriticalSkill
      .addCase(updateCriticalSkill.fulfilled, (state, action) => {
        const index = state.criticalSkills.findIndex(
          (skill) => skill.id === action.payload[0].id
        );
        if (index !== -1) {
          state.criticalSkills[index] = action.payload[0];
        }
      });
  },
});

// Export actions and reducer
export const {
  setSelectedPromptType,
  clearMessages,
  addUserMessage,
  setPrompts,
} = trainingStrategyInputSlice.actions;

export default trainingStrategyInputSlice.reducer;
